import React from "react";
import Select from "react-select";
import { sweetalert } from "../../App";
import { calculateAge } from "../../util/ValidationUtil";
import {
    buildRow,
    getLabel,
    getLabelForCounty,
    getLabelForState,
    getLabelIDsArray,
    getReactSelectAriaLabel,
    getSelectLabels,
    getlabelsFromIDs,
    isPlacesAvailable,
    parseDate,
} from "../../util/FormatUtil";
import InBetweenOverlay from "../InBetweenOverlay";
import Overlay from "../Overlay";
import { Patient } from "../../types/Patient";
import PhoneInput from 'react-phone-number-input'
import Validator, { ValidationEntry } from "../../validation/Validator";
import { Validators } from "../../validation/Validators";
import PatientAPI from "../../network/PatientAPI";
import { AuthContext } from "../../context/AuthContext";
import SystemAPI from "../../network/SystemAPI";
import { withTranslation, Trans } from 'react-i18next';
import AutoComplete from "./Autocomplete";

interface ProfileManagementState {
    profile: Patient
    showInBetween
    showLoading
    genders
    ethnicities
    races
    states
    countries
    // facilities
    selectedLanguage
    SearchAddress
    counties
    countyLabel
    stateLabel
    useManualAddress?: boolean
}
interface ProfileManagementProps {
    t
}
class ProfileManagement extends React.Component<ProfileManagementProps, ProfileManagementState> {
    static contextType = AuthContext;

    constructor(props) {
        super(props);
        this.state = {
            showInBetween: false,
            showLoading: false,
            SearchAddress: '',
            profile: {
                ID: null,
                uid: "",
                firstName: '',
                middleName: '',
                lastName: '',
                email: '',
                phoneNumber: '',
                dateOfBirth: '',
                streetAddress: '',
                city: '',
                state: null,
                zipcode: '',
                country: '',
                county: null,
                streetAddress2: '',
                customerID: null,
                genderID: null,
                ethnicityID: null,
                raceID: null,
                guardianFirstName: '',
                guardianLastName: '',
                textOptOut: false

            } as Patient,
            genders: [],
            ethnicities: [],
            races: [],
            states: [],
            countries: [],
            selectedLanguage: 'en',
            counties: [],
            countyLabel: null,
            stateLabel: null,
            useManualAddress: false,
        }
        this.modifyPatient = this.modifyPatient.bind(this);
        this.loadDataAndPatientInfo = this.loadDataAndPatientInfo.bind(this);
    }

    componentDidMount(): void {
        document.title = 'Profile Management | Oklahoma Department of Health';
        this.loadDataAndPatientInfo()
    }

    loadDataAndPatientInfo() {
        this.setState({ showLoading: true }, async () => { 
            // get all supporting data
            let races = await SystemAPI.getAllRaces()
            let genders = await SystemAPI.getAllGenders()
            let ethnicities = await SystemAPI.getAllEthnicities()
            let states = await SystemAPI.getAllStates()
            let countries = await SystemAPI.getAllCountries()
            let countiesResponse = await SystemAPI.getAllCounties()

            this.setState({ 
                races: races,
                genders: genders,
                ethnicities: ethnicities,
                states: states,
                countries: countries,
                counties: countiesResponse?.counties?.map((county: any) => {
                    return { label: county.County, value: county.State };
                })
            }, async ()=>{ 
                // set profile data now that we have supporting data
                let profileResponse = await PatientAPI.getUserProfileByUID(this.context.uid)
                let profileData = profileResponse.data[0]; 
                this.setState({
                    profile: {
                        ID: profileData && profileData.id ? profileData.id : null,
                        uid: profileData && profileData.uid ? profileData.uid : null,
                        firstName: profileData && profileData.firstName ? profileData.firstName : this.context.firstName,
                        middleName: profileData && profileData.middleName ? profileData.middleName : this.context.middleName,
                        lastName: profileData && profileData.lastName ? profileData.lastName : this.context.lastName,
                        email: profileData && profileData.email ? profileData.email : this.context.email,
                        phoneNumber: profileData && profileData.phoneNumber ? profileData.phoneNumber : null,
                        dateOfBirth: profileData && profileData.dateOfBirth ? parseDate(profileData.dateOfBirth) : null,
                        streetAddress: profileData && profileData.streetAddress ? profileData.streetAddress : null,
                        city: profileData && profileData.city ? profileData.city : null,
                        state: profileData && profileData.state ? profileData.state : null,
                        zipcode: profileData && profileData.zipcode ? profileData.zipcode : null,
                        country: profileData && profileData.country ? (profileData.country === 'United States of America' ? 'United States' : profileData.country ? profileData.country : "") : "",
                        county: profileData && profileData.county ? profileData.county : null,
                        streetAddress2: profileData && profileData.streetAddress2 ? profileData.streetAddress2 : null,
                        genderID: profileData && profileData.genderID ? profileData.genderID : null,
                        ethnicityID: profileData && profileData.ethnicityID ? profileData.ethnicityID : null,
                        raceID: profileData && profileData.raceID ? profileData.raceID : null,
                        guardianFirstName: profileData && profileData.guardianFirstName ? profileData.guardianFirstName : '',
                        guardianLastName: profileData && profileData.guardianLastName ? profileData.guardianLastName : '',
                        textOptOut: profileData && profileData.TextOptOut ? profileData.TextOptOut : false
                    } as Patient,
                    // A saved address is only visible in manual mode: the Google search box always
                    // renders empty, so in autocomplete mode the parent's own saved address looks
                    // like it was lost. Open in manual mode whenever an address is already on file.
                    useManualAddress: !!(profileData && profileData.streetAddress),
                }, ()=> {
                    // set dropdown labels now that we have supporting data, set showLoading to false
                    this.setState({
                        stateLabel: this.state.profile.state ? getLabelForState(this.state.profile.state, this.state.states) : null,
                        countyLabel: this.state.profile.county ? getLabelForCounty(this.state.profile.county, this.state.counties, this.state.profile.state, this.state.states) : null,
                        showLoading: false
                    });
                }); // set state profile
            });  // set state supporting data
        }); // set state showLoading:true
    }

    /**
     * Google reports a resolved address only through its place_changed event, so free text
     * typed into the search box never updates streetAddress/city/zipcode/county. Clearing those
     * here stops an edited search string from silently re-saving the previously resolved (or
     * already-saved) address. `state` is deliberately left alone so its dropdown label survives.
     */
    handleAddressSearchChange(addr) {
        this.setState((prevState) => ({
            SearchAddress: addr,
            profile: {
                ...prevState.profile,
                streetAddress: null,
                city: null,
                zipcode: null,
                county: null
            },
            countyLabel: null
        }))
    }

    /** Readable summary of the address that will actually be saved, or null if none is set. */
    getResolvedAddressSummary() {
        let profile = this.state.profile;
        if (!profile || !profile.streetAddress) { return null }
        let stateLabel = getLabelForState(profile.state, this.state.states, this.props.t);
        return [
            profile.streetAddress,
            profile.city,
            stateLabel && stateLabel.label ? stateLabel.label : profile.state,
            profile.zipcode,
            profile.county ? `${profile.county} ${this.props.t('County')}` : null
        ].filter(Boolean).join(', ');
    }

    handleSubmit() {
        this.setState({ showLoading: true }, () => {
            let tempDate = this.state.profile.dateOfBirth;
            if (typeof tempDate === 'object') {
                tempDate = parseDate(tempDate);
            }

            let patientInfoCopy = JSON.parse(JSON.stringify(this.state.profile));
            patientInfoCopy.firstName = patientInfoCopy.firstName ? patientInfoCopy.firstName.trim() : null;
            patientInfoCopy.middleName = patientInfoCopy.middleName ? patientInfoCopy.middleName.trim() : null;
            patientInfoCopy.lastName = patientInfoCopy.lastName ? patientInfoCopy.lastName.trim() : null;
            patientInfoCopy.email = patientInfoCopy.email ? patientInfoCopy.email.trim() : null;
            patientInfoCopy.dateOfBirth = tempDate ? tempDate : null;
            patientInfoCopy.streetAddress = patientInfoCopy.streetAddress ? patientInfoCopy.streetAddress.trim() : null;
            patientInfoCopy.streetAddress2 = patientInfoCopy.streetAddress2 ? patientInfoCopy.streetAddress2.trim() : null;
            patientInfoCopy.city = patientInfoCopy.city ? patientInfoCopy.city.trim() : null;
            patientInfoCopy.zipcode = patientInfoCopy.zipcode ? patientInfoCopy.zipcode.trim() : null;
            patientInfoCopy.county = patientInfoCopy.county ? patientInfoCopy.county.trim() : null;
            patientInfoCopy.country = patientInfoCopy.country ? patientInfoCopy.country.trim() : null;
            patientInfoCopy.phoneNumber = patientInfoCopy.phoneNumber ? patientInfoCopy.phoneNumber.trim() : null;
            patientInfoCopy.guardianFirstName = patientInfoCopy.guardianFirstName ? patientInfoCopy.guardianFirstName.trim() : '';
            patientInfoCopy.guardianLastName = patientInfoCopy.guardianLastName ? patientInfoCopy.guardianLastName.trim() : '';

            this.setState({ profile: patientInfoCopy });

            //validate patient info
            //validate child info
            let res = this.validatePatient(patientInfoCopy)
            if (!res.success) {
                this.setState({ showLoading: false })
                return sweetalert.fire({
                    icon: "error",
                    title: "",
                    text: this.props.t(res.error),
                });
            } else {
                this.setState({ showLoading: false })
                this.modifyPatient(this.state.profile);
            }

        })
    }

    validatePatient(s) {
        let patientValidation = {
            PatientFirstName: s.firstName,
            PatientMiddleName: s.middleName,
            PatientLastName: s.lastName,
            PatientDOB: s.dateOfBirth,
            PatientEmail: s.email,
            PatientGender: s.genderID,
            PatientAddress: s.streetAddress,
            PatientAddress2: s.streetAddress2,
            PatientCity: s.city,
            PatientZip: s.zipcode,
            PatientState: s.state,
            PatientCounty: s.county,
            PatientCountry: s.country,
            PatientPhone: s.phoneNumber,
            PatientEthnicity: s.ethnicityID,
            PatientRace: s.raceID,
            GuardianFirstName: s.guardianFirstName,
            GuardianLastName: s.guardianLastName,
        }

        let validator = new Validator<any>()
            .withValidation("PatientFirstName", Validators.requireNotBlankAndLength(50, "First Name"));
        if (s.middleName && s.middleName.trim().length > 0) {
            validator = validator.withValidation("PatientMiddleName", Validators.requireNotBlankAndLength(50, "Middle Name"));
        }
        validator = validator.withValidation("PatientLastName", Validators.requireNotBlankAndLength(50, "Last Name"))
            .withValidation("PatientDOB", Validators.requireDOB(150, "Date of Birth"))
            .withComposedValidation("PatientEmail", new ValidationEntry(Validators.requireValidEmail("Email")), Validators.requireNotBlankAndLength(100, "Email"))
            .withSimpleValidation("PatientGender", Validators.requireNonNullValidator("Gender"))
            .withSimpleValidation("PatientAddress", Validators.requireNonNullValidator("Address"))
            .withSimpleValidation("PatientCity", Validators.requireNonNullValidator("City"))
            .withSimpleValidation("PatientState", Validators.requireNonNullValidator("State"))
            .withSimpleValidation("PatientZip", Validators.requireNonNullValidator("Zipcode"))
            .withSimpleValidation("PatientCounty", Validators.requireNonNullValidator("County"))

        validator = validator.withComposedValidation("PatientPhone", new ValidationEntry(Validators.requireNonNullValidator("Phone")), new ValidationEntry(Validators.requirePhone("Phone")))
            .withSimpleValidation("PatientEthnicity", Validators.requireNonNullValidator("Ethnicity"))
            .withSimpleValidation("PatientRace", Validators.requireNonNullValidator("Race"));
        let validationResponse = validator.validate(patientValidation);
        if (!validationResponse.success) {
            return { success: false, error: validationResponse.error }
        }

        return { success: true };
    }

    modifyPatient(patient: Patient) {
        patient.guardianFirstName = patient.guardianFirstName ? patient.guardianFirstName : null;
        patient.guardianLastName = patient.guardianLastName ? patient.guardianLastName : null;

        this.setState({ showLoading: true }, async () => {
            try {
                let result = await PatientAPI.editPatient(patient);
                if (result.success) {
                    sweetalert.fire({ icon: 'success', title: '', text: this.props.t('Profile saved.') }).then(() => {
                        // send back to homepage
                        //@ts-ignore
                        window['location'] = '/admin' as unknown as Location;
                    });
                } else {
                    sweetalert.fire({ icon: 'error', title: '', text: result.reason });
                    this.setState({ showLoading: false });
                }
            }
            catch (e) {
                console.log(e);
                this.setState({ showLoading: false });
            }
        });
    }

    render() {

        let countriesCopy = this.state.countries && this.state.countries.length > 0 ? JSON.parse(JSON.stringify(this.state.countries)) : [];
        let indexOfUS = countriesCopy.findIndex(obj => obj.value === 'United States');
        let [removedObj] = countriesCopy.splice(indexOfUS, 1);
        countriesCopy.unshift(removedObj)

        let translationGenders = this.state.genders && this.state.genders.length > 0 ? this.state.genders.map(e => { return { label: this.props.t(e.label), value: e.value } }) : [];
        let translationEthnicities = this.state.ethnicities && this.state.ethnicities.length > 0 ? this.state.ethnicities.map(e => { return { label: this.props.t(e.label), value: e.value } }) : [];
        let translationRaces = this.state.races && this.state.races.length > 0 ? this.state.races.map(e => { return { label: this.props.t(e.label), value: e.value } }) : [];

        return (
            <>
                <Overlay show_loading={this.state.showLoading} zIndex={100003} />
                <InBetweenOverlay showInBetween={this.state.showInBetween} zIndex={100002} />
                {!this.state.showLoading &&
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-12 offset-lg-1 col-sm-12 col-md-12 col-lg-10 col-xl-10 pt-2">
                                <main id="main-content" tabIndex={-1} aria-label={this.props.t("Profile Management")}>
                                    <div className={"card"}>
                                        <div className="card-header">
                                            <h5 className="card-title d-inline-block" id="result_card_label">{this.props.t("Profile Management")}</h5>
                                        </div>
                                        <div className="card-body row">
                                            <div className="col-xl-6 col-lg-12 col-md-12 col-sm-12 col-xs-12">
                                                {buildRow(this.props.t("Email"),
                                                    <input className={"form-control"}
                                                        id="EmailInput"
                                                        maxLength={50}
                                                        autoComplete={"off"}
                                                        type={"search"}
                                                        name={"Email"}
                                                        aria-label="Email"
                                                        placeholder="name@example.com"
                                                        onChange={(e) => {
                                                            this.setState((prevState) => ({
                                                                profile: {
                                                                    ...prevState.profile,
                                                                    email: e.target.value
                                                                }
                                                            }))
                                                        }}
                                                        value={this.state.profile.email}
                                                    />
                                                    , this.props.t('Email')
                                                    , true
                                                )}
                                                {buildRow(this.props.t("First Name"),
                                                    <input
                                                        id="FirstNameInput"
                                                        className={"form-control"}
                                                        maxLength={50}
                                                        autoComplete={"off"}
                                                        aria-label="First Name"
                                                        type={"search"}
                                                        name={"First Name"}
                                                        onChange={(e) => {
                                                            this.setState((prevState) => ({
                                                                profile: {
                                                                    ...prevState.profile,
                                                                    firstName: e.target.value
                                                                }
                                                            }))
                                                        }}
                                                        value={this.state.profile.firstName}
                                                    />
                                                    , this.props.t('First Name')
                                                    , true
                                                )}
                                                {buildRow(this.props.t('Middle Name'),
                                                    <input
                                                        id="MiddleNameInput"
                                                        className={"form-control"}
                                                        maxLength={50}
                                                        autoComplete={"off"}
                                                        type={"search"}
                                                        name={"Middle Name"}
                                                        aria-label="Middle Name"
                                                        onChange={(e) => {
                                                            this.setState((prevState) => ({
                                                                profile: {
                                                                    ...prevState.profile,
                                                                    middleName: e.target.value
                                                                }
                                                            }))
                                                        }}
                                                        value={this.state.profile.middleName}
                                                    />
                                                    , this.props.t('Middle Name')
                                                )}
                                                {buildRow(this.props.t('Last Name'),
                                                    <input
                                                        id="LastNameInput"
                                                        className={"form-control"}
                                                        maxLength={50}
                                                        autoComplete={"off"}
                                                        type={"search"}
                                                        name={"Last Name"}
                                                        aria-label="Last Name"
                                                        onChange={(e) => {
                                                            this.setState((prevState) => ({
                                                                profile: {
                                                                    ...prevState.profile,
                                                                    lastName: e.target.value
                                                                }
                                                            }))
                                                        }}
                                                        value={this.state.profile.lastName}
                                                    />
                                                    , this.props.t('Last Name')
                                                    , true
                                                )}
                                                <div className="form-group row" data-toggle={'tooltip'} data-placement={'top'} title={'Date of Birth'}>
                                                    <label htmlFor={'PatientDOB'} className="col-12 col-sm-4 col-form-label text-left">{this.props.t('Date of Birth')}<section className="text-danger d-inline-block px-1">*</section></label>
                                                    <div className="col-12 col-sm-8 p-0 m-0 text-center text-md-left" id={'PatientDOB'}>
                                                        <input
                                                            id="PatientDOBInput"
                                                            min={'1000-01-01'}
                                                            max={new Date().toISOString().split('T')[0]}
                                                            className={'form-control'}
                                                            autoComplete={'off'}
                                                            type={'date'}
                                                            name={'PatientDOB'}
                                                            aria-label="Date of Birth"
                                                            value={this.state.profile && this.state.profile.dateOfBirth ? new Date(this.state.profile.dateOfBirth).toISOString().split('T')[0] : ''}
                                                            onChange={(e) => {
                                                                const inputValue = e.target.value;
                                                                const [year, month, day] = inputValue.split('-');
                                                                if (Date.parse(inputValue)) {
                                                                    if (year && year.length > 4) {
                                                                        const correctedYear = year.slice(0, 4);
                                                                        const newDateValue = `${correctedYear}-${month}-${day}`;
                                                                        this.setState((prevState) => ({
                                                                            profile: {
                                                                                ...prevState.profile,
                                                                                dateOfBirth: new Date(newDateValue)
                                                                            }
                                                                        }));
                                                                    } else {
                                                                        this.setState((prevState) => ({
                                                                            profile: {
                                                                                ...prevState.profile,
                                                                                dateOfBirth: new Date(inputValue)
                                                                            }
                                                                        }));
                                                                    }
                                                                }
                                                            }
                                                            }
                                                        >
                                                        </input>
                                                    </div>
                                                </div>
                                                {buildRow(this.props.t("Gender"),
                                                    <Select
                                                        isSearchable={true}
                                                        placeholder={<div className="accessibilityText">Please Select...</div>}
                                                        noOptionsMessage={() => "No option"}
                                                        value={getLabel(this.state.profile.genderID, this.state.genders, this.props.t)}
                                                        aria-label={getReactSelectAriaLabel("Gender", getLabel(this.state.profile.genderID, this.state.genders, this.props.t), this.props.t)}
                                                        onChange={(e) => this.setState((prevState) => ({
                                                            profile: {
                                                                ...prevState.profile,
                                                                genderID: e.value
                                                            }
                                                        }))}
                                                        className={"state_select"}
                                                        options={translationGenders}
                                                    />
                                                    , this.props.t("Gender")
                                                    , true
                                                )}
                                                {buildRow(this.props.t("Ethnicity"),
                                                    <Select
                                                        isSearchable={true}
                                                        placeholder={<div className="accessibilityText">Please Select...</div>}
                                                        noOptionsMessage={() => "No option"}
                                                        value={getLabel(this.state.profile.ethnicityID, this.state.ethnicities, this.props.t)}
                                                        aria-label={getReactSelectAriaLabel("Ethnicity", getLabel(this.state.profile.ethnicityID, this.state.ethnicities, this.props.t), this.props.t)}
                                                        onChange={(e) => this.setState((prevState) => ({
                                                            profile: {
                                                                ...prevState.profile,
                                                                ethnicityID: e.value
                                                            }
                                                        }))}
                                                        className={"state_select"}
                                                        options={translationEthnicities}
                                                    />
                                                    , this.props.t("Ethnicity")
                                                    , true
                                                )}
                                                {buildRow(this.props.t("Race"),
                                                    <Select
                                                        isSearchable={true}
                                                        placeholder={<div className="accessibilityText">Please Select...</div>}
                                                        noOptionsMessage={() => "No option"}
                                                        value={getLabel(this.state.profile.raceID, this.state.races, this.props.t)}
                                                        aria-label={getReactSelectAriaLabel("Race", getLabel(this.state.profile.raceID, this.state.races, this.props.t), this.props.t)}
                                                        onChange={(e) => this.setState((prevState) => ({
                                                            profile: {
                                                                ...prevState.profile,
                                                                raceID: e.value
                                                            }
                                                        }))}
                                                        className={"state_select"}
                                                        options={translationRaces}
                                                    />
                                                    , this.props.t("Race")
                                                    , true
                                                )}
                                            </div>
                                            <div className="col-xl-6 col-lg-12 col-md-12 col-sm-12 col-xs-12">
                                                {buildRow(this.props.t("Phone Number"),
                                                    <PhoneInput
                                                        id={'PhoneNumberInput'}
                                                        placeholder={this.props.t("Enter phone number (10 digit)")}
                                                        aria-label={this.props.t("Phone Number")}
                                                        value={this.state.profile.phoneNumber}
                                                        onChange={(e) => this.setState((prevState) => ({
                                                            profile: { ...prevState.profile, phoneNumber: e }
                                                        }))}
                                                        defaultCountry="US"
                                                    />, this.props.t("Phone Number")
                                                    , true
                                                )}

                                                {/* Address Section with Toggle */}
                                                <div key={"ProfileAddressToggle"} className="form-group row" data-toggle={'tooltip'} data-placement={'top'} title={this.props.t('Address')}>
                                                    {/* htmlFor tracks the mode: the autocomplete input is not rendered in manual
                                                        mode, so a fixed target would leave the label unassociated half the time. */}
                                                    <label id={'AddressLabel'} htmlFor={this.state.useManualAddress ? 'StreetAddress' : 'Parentautocomplete'} className="col-12 col-sm-4 col-form-label px-3">
                                                        {this.props.t('Address')}<section className="text-danger d-inline-block px-1">*</section>
                                                    </label>
                                                    {/* Manual mode renders five controls, so the group — not just the first input —
                                                        is what the label names. role/aria-labelledby announces that correctly. */}
                                                    <div className="col-12 col-sm-8 p-0 m-0" role="group" aria-labelledby={'AddressLabel'}>
                                                        {/* No toggle when Places is unavailable — there is nothing to switch to,
                                                            and manual entry is already the only mode being rendered. */}
                                                        {isPlacesAvailable() &&
                                                        <div className="d-flex justify-content-end mb-1 px-2">
                                                            <button
                                                                type="button"
                                                                className="btn btn-link text-muted p-0"
                                                                style={{ fontSize: '0.9rem' }}
                                                                aria-pressed={this.state.useManualAddress}
                                                                onClick={() => this.setState(prev => ({ useManualAddress: !prev.useManualAddress }))}
                                                            >
                                                                {!this.state.useManualAddress ? this.props.t('Enter Manually') : this.props.t('Search Address')}
                                                            </button>
                                                        </div>
                                                        }

                                                        {/* isPlacesAvailable is re-evaluated on every render, so this self-corrects
                                                            once the async Maps script finishes loading. */}
                                                        {!this.state.useManualAddress && isPlacesAvailable() ? (
                                                            /* Google Places Autocomplete Mode */
                                                            <React.Fragment>
                                                            <AutoComplete key={"Parentautocomplete"} id={"Parentautocomplete"}
                                                                onChange={(addr) => this.handleAddressSearchChange(addr)}
                                                                value={this.state.SearchAddress}
                                                                updateFormData={(addr, city, state, zip, county, country) => {
                                                                    this.setState({
                                                                        profile: { ...this.state.profile, streetAddress: addr, city: city, state: state, zipcode: zip, county: county },
                                                                        stateLabel: getLabelForState(state, this.state.states, this.props.t),
                                                                        countyLabel: getLabelForCounty(county, this.state.counties, state, this.state.states, this.props.t)
                                                                    })
                                                                }}
                                                            />
                                                            {/* The search box only shows what was typed, not what was resolved. Echo the
                                                                address that will actually be saved so the parent can always see and confirm it. */}
                                                            {this.getResolvedAddressSummary() &&
                                                                <div className="px-2 pt-2 text-muted" style={{ fontSize: '0.9rem' }} aria-live="polite">
                                                                    {this.props.t('Address to be saved')}: {this.getResolvedAddressSummary()}
                                                                </div>
                                                            }
                                                            </React.Fragment>
                                                        ) : (
                                                            /* Manual Address Entry Mode */
                                                            <div className="px-2">
                                                                {/* Visible labels, not just placeholders: a placeholder disappears once the
                                                                    field has a value, leaving a filled form with unlabelled boxes (WCAG 3.3.2). */}
                                                                <div className="mb-2">
                                                                    <label htmlFor={'StreetAddress'} className="mb-0 small text-muted">{this.props.t("Street Address")}</label>
                                                                    <input
                                                                        id={'StreetAddress'}
                                                                        className={"form-control"}
                                                                        maxLength={50}
                                                                        autoComplete={"off"}
                                                                        type={"search"}
                                                                        name={'StreetAddress'}
                                                                        placeholder={this.props.t("Street Address")}
                                                                        aria-label={this.props.t("Street Address")}
                                                                        onChange={(e) => {
                                                                            this.setState((prevState) => ({
                                                                                profile: {
                                                                                    ...prevState.profile,
                                                                                    streetAddress: e.target.value
                                                                                }
                                                                            }))
                                                                        }}
                                                                        value={this.state.profile?.streetAddress}
                                                                    />
                                                                </div>
                                                                <div className="mb-2">
                                                                    <label htmlFor={'City'} className="mb-0 small text-muted">{this.props.t("City")}</label>
                                                                    <input
                                                                        id={'City'}
                                                                        className={"form-control"}
                                                                        maxLength={50}
                                                                        autoComplete={"off"}
                                                                        type={"search"}
                                                                        name={'City'}
                                                                        placeholder={this.props.t("City")}
                                                                        aria-label={this.props.t("City")}
                                                                        onChange={(e) => {
                                                                            this.setState((prevState) => ({
                                                                                profile: {
                                                                                    ...prevState.profile,
                                                                                    city: e.target.value
                                                                                }
                                                                            }))
                                                                        }}
                                                                        value={this.state.profile?.city}
                                                                    />
                                                                </div>
                                                                <div className="row mb-2">
                                                                    <div className="col-6">
                                                                        {/* inputId, not id: react-select puts id on the wrapper, so htmlFor must
                                                                            target the inner input for the label to associate. */}
                                                                        <label htmlFor={'StateSelectInput'} className="mb-0 small text-muted">{this.props.t("State")}</label>
                                                                        <Select
                                                                            key={'State'}
                                                                            id={'State'}
                                                                            inputId={'StateSelectInput'}
                                                                            isSearchable={true}
                                                                            placeholder={<div className="accessibilityText">{this.props.t("State")}</div>}
                                                                            noOptionsMessage={() => this.props.t("No option")}
                                                                            aria-label={this.props.t("State")}
                                                                            value={this.state.stateLabel}
                                                                            onChange={(e) => {
                                                                                this.setState((prevState) => ({
                                                                                    profile: {
                                                                                        ...prevState.profile,
                                                                                        state: e.value,
                                                                                        // Clearing countyLabel alone only blanks the dropdown — profile.county
                                                                                        // is what handleSubmit saves and what the County validator checks, so
                                                                                        // the stale county was still written under the new state.
                                                                                        county: null
                                                                                    },
                                                                                    stateLabel: e,
                                                                                    countyLabel: null,
                                                                                }))
                                                                            }}
                                                                            className={'state_select'}
                                                                            options={this.state.states}
                                                                        />
                                                                    </div>
                                                                    <div className="col-6">
                                                                        <label htmlFor={'Zipcode'} className="mb-0 small text-muted">{this.props.t("Zipcode")}</label>
                                                                        <input
                                                                            id={'Zipcode'}
                                                                            className={"form-control"}
                                                                            maxLength={10}
                                                                            autoComplete={"off"}
                                                                            type={"search"}
                                                                            name={'Zipcode'}
                                                                            placeholder={this.props.t("Zipcode")}
                                                                            aria-label={this.props.t("Zipcode")}
                                                                            onChange={(e) => {
                                                                                this.setState((prevState) => ({
                                                                                    profile: {
                                                                                        ...prevState.profile,
                                                                                        zipcode: e.target.value
                                                                                    }
                                                                                }))
                                                                            }}
                                                                            value={this.state.profile?.zipcode}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className="mb-2">
                                                                    <label htmlFor={'CountySelectInput'} className="mb-0 small text-muted">{this.props.t("County")}</label>
                                                                    <Select
                                                                        key={`county-select-${this.state.profile.state ? (typeof this.state.profile.state === 'object' ? this.state.profile.state.value : this.state.profile.state) : 'no-state'}`}
                                                                        id={'County'}
                                                                        inputId={'CountySelectInput'}
                                                                        isSearchable={true}
                                                                        placeholder={<div className="accessibilityText">{this.props.t("County")}</div>}
                                                                        noOptionsMessage={() => this.props.t("No option")}
                                                                        aria-label={this.props.t("County")}
                                                                        value={this.state.countyLabel}
                                                                        options={this.state.profile.state
                                                                            ? this.state.counties.filter(c => {
                                                                                const stateValue = typeof this.state.profile.state === 'object'
                                                                                    ? this.state.profile.state.value
                                                                                    : this.state.profile.state;
                                                                                // Google can return a state/region short code that is not in our states list
                                                                                // (another country, an unlisted territory). Without the guard, .label on the
                                                                                // undefined result throws during render and blanks the page. Matching
                                                                                // ParentInfoModal, an unresolved state yields no county options instead.
                                                                                const stateName = this.state.states.find(s => s.value === stateValue)?.label;
                                                                                return c.value === stateName || c.originalValue === stateName;
                                                                            }).map(c => ({
                                                                                label: c.label,
                                                                                value: c.label,
                                                                                originalValue: c.value
                                                                            }))
                                                                            : this.state.counties.map(c => ({
                                                                                label: c.label,
                                                                                value: c.label,
                                                                                originalValue: c.value
                                                                            }))
                                                                        }
                                                                        filterOption={(option, input) => {
                                                                            const label = option.label?.toString().toLowerCase() || '';
                                                                            const search = input.toLowerCase();
                                                                            return label.includes(search);
                                                                        }}
                                                                        onChange={(e) => {
                                                                            this.setState((prevState) => ({
                                                                                profile: {
                                                                                    ...this.state.profile,
                                                                                    county: e.label
                                                                                },
                                                                                countyLabel: e
                                                                            }))
                                                                        }}
                                                                        className={'state_select'}
                                                                    />
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                {buildRow(this.props.t('Apartment/Suite/Building #'),
                                                    <input
                                                        id={'SecondaryAddressLine'}
                                                        className={"form-control"}
                                                        maxLength={50}
                                                        autoComplete={"off"}
                                                        type={"search"}
                                                        name={'SecondaryAddressLine'}
                                                        aria-label={this.props.t("Apartment/Suite/Building #")}
                                                        onChange={(e) => {
                                                            this.setState((prevState) => ({
                                                                profile: {
                                                                    ...prevState.profile,
                                                                    streetAddress2: e.target.value
                                                                }
                                                            }))
                                                        }}
                                                        value={this.state.profile?.streetAddress2}
                                                    />, this.props.t('Apartment/Suite/Building #')
                                                )}
                                                <div className="form-check text-center">
                                                    <input className="form-check-input" type="checkbox" id="optOut"
                                                        checked={this.state.profile?.textOptOut ? true : false}
                                                        onChange={(e) =>
                                                            this.setState((prevState) => ({
                                                                profile: {
                                                                    ...prevState.profile,
                                                                    textOptOut: e.target.checked
                                                                }
                                                            }))} />
                                                    <label className="form-check-label" htmlFor="optOut">
                                                        Opt out of text messages about the status of my immunization exemption submission.
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="card-footer">
                                            <button type="button" className="btn btn-primary" onClick={() => { this.handleSubmit() }}> {this.props.t("Submit")} </button>
                                            <p className="float-right d-inline-block m-0"><section className="text-danger d-inline-block px-1">*</section>{this.props.t("Required Fields")}</p>
                                        </div>
                                    </div>
                                </main>
                            </div>
                        </div>
                    </div>}
            </>
        )
    }
}

export default withTranslation()(ProfileManagement)